import { ref, type Ref } from 'vue';

/**
 * One shared ticking clock for every NxRelativeTime on the page. It runs only
 * while at least one instance is mounted, ticks every 30 s while any of them
 * shows something under an hour old, else once a minute.
 */
const now = ref(Date.now());
const fast = new Set<symbol>();
let subscribers = 0;
let timer: ReturnType<typeof setTimeout> | undefined;

function schedule() {
  clearTimeout(timer);
  if (subscribers === 0) {
    timer = undefined;
    return;
  }
  timer = setTimeout(
    () => {
      now.value = Date.now();
      schedule();
    },
    fast.size > 0 ? 30_000 : 60_000
  );
}

export interface ClockSubscription {
  now: Readonly<Ref<number>>;
  /** Mark this subscriber as showing a recent time (asks for the fast tick). */
  setFast: (value: boolean) => void;
  release: () => void;
}

export function subscribeClock(): ClockSubscription {
  const id = Symbol('nx-relative-time');
  subscribers += 1;
  now.value = Date.now();
  if (subscribers === 1) schedule();
  return {
    now,
    setFast(value) {
      const had = fast.size > 0;
      if (value) fast.add(id);
      else fast.delete(id);
      if (had !== fast.size > 0) schedule();
    },
    release() {
      fast.delete(id);
      subscribers = Math.max(0, subscribers - 1);
      schedule();
    },
  };
}

/** Test hook: whether the clock currently runs. */
export function isClockRunning(): boolean {
  return timer !== undefined;
}
